import type { Session } from "@/lib/session";

export interface ThreadTitleCacheEntry {
  title: string;
  lastActiveAt: string | null;
  source: "seed" | "anchor" | "detail";
}

const TITLE_SOURCE_PRIORITY: Record<ThreadTitleCacheEntry["source"], number> = {
  // An anchor request may have started before the detail reconciliation but
  // finish afterward with the same last_active_at. Prefer the detail in that
  // tie so a delayed "New Thread" value cannot restore the placeholder.
  seed: 0,
  anchor: 1,
  detail: 2,
};

export function isOlderLastActiveAt(
  incomingLastActiveAt: string | null,
  currentLastActiveAt: string | null,
): boolean {
  if (!incomingLastActiveAt || !currentLastActiveAt) return false;
  const incomingTime = Date.parse(incomingLastActiveAt);
  const currentTime = Date.parse(currentLastActiveAt);
  return Number.isFinite(incomingTime)
    && Number.isFinite(currentTime)
    && incomingTime < currentTime;
}

export function mergeThreadTitleCacheEntry(
  current: ThreadTitleCacheEntry | undefined,
  incoming: ThreadTitleCacheEntry,
): ThreadTitleCacheEntry {
  if (!current) return incoming;

  const currentTime = current.lastActiveAt
    ? Date.parse(current.lastActiveAt)
    : Number.NaN;
  const incomingTime = incoming.lastActiveAt
    ? Date.parse(incoming.lastActiveAt)
    : Number.NaN;
  if (isOlderLastActiveAt(incoming.lastActiveAt, current.lastActiveAt)) {
    return current;
  }
  if (
    currentTime === incomingTime
    && TITLE_SOURCE_PRIORITY[incoming.source] < TITLE_SOURCE_PRIORITY[current.source]
  ) {
    return current;
  }

  if (
    current.title === incoming.title
    && current.lastActiveAt === incoming.lastActiveAt
    && current.source === incoming.source
  ) {
    return current;
  }
  return incoming;
}

interface ThreadSessionReconciliationOptions {
  fetchSession: (childSessionId: string) => Promise<Session>;
  isActiveSession: (childSessionId: string) => boolean;
  updateTitle: (session: Session) => void;
  updateActiveSession: (session: Session) => void;
  refreshChildren: (parentSessionId: string) => void;
  onError?: (error: unknown) => void;
}

export function reconcileThreadSessionOnce(
  childSessionId: string,
  inFlightRequests: Map<string, Promise<Session | null>>,
  options: ThreadSessionReconciliationOptions,
): Promise<Session | null> {
  const inFlight = inFlightRequests.get(childSessionId);
  if (inFlight) return inFlight;

  const request = (async (): Promise<Session | null> => {
    try {
      const session = await options.fetchSession(childSessionId);
      if (session.id !== childSessionId || session.session_type !== "thread") {
        return null;
      }

      options.updateTitle(session);
      if (options.isActiveSession(childSessionId)) {
        options.updateActiveSession(session);
      }
      if (session.parent_session_id) {
        options.refreshChildren(session.parent_session_id);
      }
      return session;
    } catch (error) {
      options.onError?.(error);
      return null;
    }
  })();

  inFlightRequests.set(childSessionId, request);
  const clearRequest = () => {
    if (inFlightRequests.get(childSessionId) === request) {
      inFlightRequests.delete(childSessionId);
    }
  };
  void request.then(clearRequest, clearRequest);
  return request;
}

export function refreshSessionAfterRunCompletion(
  activeSession: Session | undefined,
  reconcileThreadSession: (childSessionId: string) => Promise<Session | null>,
  refreshSessions: () => void | Promise<unknown>,
): void {
  if (activeSession?.session_type === "thread") {
    void reconcileThreadSession(activeSession.id);
  } else {
    void refreshSessions();
  }
}
